// The CREATIVE BRIEF follows CLIENT concept approval. It has its own review,
// versions and approval before design/production and publishing.
//
// An idea is a title, a synopsis and three hooks. A production brief is that idea
// expanded into the document a producer works from. Once a brief HAS a body the AI
// never touches it again — a declined one is rewritten BY HAND — so everything here
// is shaped around editing rather than regenerating. The single exception is a
// brief whose generation FAILED: it has no body, so retrying costs nothing.
//
// See creative-os-backend/docs/production-brief-plan.md.

import type { BriefAcceptanceSource, CommentThreadExtras, ReferenceMediaItem } from "@/types/contentCalendar.type";

/** Which body shape a brief carries. Frozen from the slot's mediaType at generation. */
export type CalendarBriefKind = "VIDEO" | "STATIC";

export type CalendarBriefStatus =
  | "GENERATING"
  | "DRAFT"
  | "IN_REVIEW"
  | "CHANGES_REQUESTED"
  | "ACCEPTED"
  | "FAILED";

/** Statuses the operator may edit. FAILED is here on purpose: a run that produced nothing leaves you to write v1 (or retry). */
export const BRIEF_EDITABLE_STATUSES: CalendarBriefStatus[] = [
  "DRAFT",
  "CHANGES_REQUESTED",
  "FAILED",
];

/** A brief in one of these blocks the whole month's send. */
export const BRIEF_BLOCKS_SEND: CalendarBriefStatus[] = ["GENERATING", "FAILED"];

// ---- Bodies ----

/**
 * Per-platform posting copy, keyed by CalendarPlatform — the brief's
 * distribution section (docs/scheduling-auto-publish-plan.md §7). Approved by
 * the client with the creative; prefilled into each Publication at upload time.
 * Optional on the body because briefs written before scheduling shipped have none.
 */
export interface DistributionEntry {
  /** Every language the brand publishes in, one per paragraph. */
  caption: string;
  hashtags: string[];
  /** YOUTUBE only. */
  title?: string;
  description?: string;
  tags?: string[];
}

export type Distribution = Record<string, DistributionEntry>;

/** Operational fields that turn an approved idea into work a team can assign. */
export interface ProductionMeta {
  owner: string;
  productionLead: string;
  productionDate: string;
  internalReviewDate: string;
  clientDueDate: string;
  /**
   * IANA zone the shoot day's clock times are read in. Display only — the
   * brand's own calendar timezone is what publishing uses, and these two must
   * never be conflated.
   */
  timezone?: string;
}

// ---- Per-shot / per-panel visual (backend issue #70) ----

/** What the picker sends: identity only. The server writes the rest. */
export type BriefVisualPick =
  | { kind: "media"; mediaId: string }
  | { kind: "reference"; referenceId: string }
  | { kind: "trend"; trendExampleId: string }
  | { kind: "custom"; customMediaId: string };

/**
 * What a shot or panel STORES. Identity plus a snapshot of the label and
 * permalink, so a signed-off revision still captions correctly after the source
 * row is deleted. Never a URL that expires — those are resolved per read.
 */
export interface BriefVisualSource {
  kind: "media" | "reference" | "trend" | "custom";
  mediaType?: "image" | "video";
  trendId?: string;
  trendExampleId?: string;
  referenceId?: string;
  mediaId?: string;
  /** A link pasted for this brief alone, saved to our storage but in no library. */
  customMediaId?: string;
  label?: string;
  pageUrl?: string | null;
  thumbnailUrl?: string | null;
}

/**
 * What a read RETURNS beside `visualSource`. Read-only: it is stripped by the
 * body schema on save, which is what keeps an expiring URL out of a revision.
 */
export interface ResolvedBriefVisual {
  source: BriefVisualSource;
  label: string | null;
  mediaType: "image" | "video";
  imageUrl: string | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  pageUrl: string | null;
  /** Every file of a custom link, in order; more than one is a carousel. Empty for the libraries. */
  items?: ReferenceMediaItem[];
  /** The source row is gone. The snapshot still reads; there is nothing to show. */
  unavailable: boolean;
}

/**
 * ONE ROW of the shoot-day schedule (backend issue #69).
 *
 * `startTime` + `durationMinutes` are the structured pair; the end is DERIVED
 * and never stored, so reordering rows cannot leave a stale end behind.
 *
 * `time` is the original free-text field and stays optional for the rows that
 * predate the pair — a note like "15 May, client availability" that no clock
 * control can represent. Never write both.
 *
 * These minutes are shoot-day minutes. `StoryboardScene.duration` is screen
 * time. They are unrelated.
 */
export interface ProductionScheduleItem {
  /** Stable row identity for reorder and diff. The client mints it. */
  id?: string;
  /** Legacy free-text timing. */
  time?: string;
  /** "HH:mm", 24-hour. */
  startTime?: string;
  /** Whole minutes, 1..1440. */
  durationMinutes?: number;
  activity: string;
  owner: string;
}

/**
 * A deliverable row. `id` is an EDITOR-LOCAL key so a list can reorder without
 * remounting every row below; it is stripped before save (the brief body has no
 * id on this shape) and so never survives a reload.
 */
export interface ProductionDeliverable {
  id?: string;
  channel: string;
  asset: string;
  aspectRatio: string;
  dimensions: string;
  durationOrPages: string;
  fileFormat: string;
  notes: string;
}

/** A VO cue. `id` is an editor-local key, stripped before save. */
export interface ProductionScriptCue {
  id?: string;
  timecode: string;
  line: string;
}

export interface StoryboardScene {
  visual: string;
  action: string;
  sound: string;
  textOnScreen: string;
  /** Optional production detail. Older briefs remain valid without it. */
  title?: string;
  framing?: string;
  duration?: string;
  productionNote?: string;
  location?: string;
  talent?: string;
  voiceover?: string;
  referenceLabel?: string;
  referenceUrl?: string;
  /** Stable row identity for reorder and diff. */
  id?: string;
  /** The picked visual. `framing` above is untouched and never derived from these. */
  visualSource?: BriefVisualSource;
  shotSize?: string;
  cameraAngle?: string;
  cameraMovement?: string;
  lens?: string;
  equipment?: string;
  /** Sourcing for THIS shot. The body's top-level lists stay the list of record. */
  props?: string[];
  wardrobe?: string[];
  /**
   * Resolved on read, stripped on save. Never send it back.
   *
   * NOT `visual` — a scene already has one, and it is the prose describing what
   * the camera sees.
   */
  resolvedVisual?: ResolvedBriefVisual;
}

export interface ProductionVideoBody {
  title: string;
  synopsis: string;
  hookVo: string;
  conceptCues: string[];
  ctaTextOnScreen: string;
  storyboard: StoryboardScene[];
  distribution?: Distribution;
  props: string[];
  wardrobe: string[];
  locations: string[];
  /** The approved constraints production must not reinterpret. */
  conceptLock?: string[];
  productionMeta?: ProductionMeta;
  talent?: string[];
  crew?: string[];
  schedule?: ProductionScheduleItem[];
  script?: ProductionScriptCue[];
  soundDirection?: string;
  deliverables?: ProductionDeliverable[];
  legalMandatories?: string[];
}

export interface ProductionPanel {
  role: string;
  composition: string;
  subject: string;
  copyOnPanel: string;
  imageryNote: string;
  aspectRatio?: string;
  dimensions?: string;
  assets?: string;
  retouching?: string;
  referenceLabel?: string;
  referenceUrl?: string;
  /** Stable row identity — the carousel's order is the array's. */
  id?: string;
  visualSource?: BriefVisualSource;
  /** Resolved on read, stripped on save. Never send it back. */
  resolvedVisual?: ResolvedBriefVisual;
}

export interface ProductionStaticBody {
  title: string;
  synopsis: string;
  headline: string;
  panels: ProductionPanel[];
  ctaTextOnArtwork: string;
  artDirection: {
    palette: string[];
    typography: string;
    moodWords: string[];
    doNots: string[];
  };
  caption: string;
  altText: string;
  distribution?: Distribution;
  props: string[];
  wardrobe: string[];
  locations: string[];
  conceptLock?: string[];
  productionMeta?: ProductionMeta;
  talent?: string[];
  crew?: string[];
  schedule?: ProductionScheduleItem[];
  deliverables?: ProductionDeliverable[];
  legalMandatories?: string[];
}

export type ProductionBriefBody = ProductionVideoBody | ProductionStaticBody;

export function isStaticBody(
  kind: CalendarBriefKind,
  body: ProductionBriefBody | null | undefined,
): body is ProductionStaticBody {
  return kind === "STATIC" && !!body;
}

/** Fields both shapes carry — the sourcing block and the header render from these alone. */
export interface SharedBriefFields {
  title: string;
  synopsis: string;
  props: string[];
  wardrobe: string[];
  locations: string[];
}

export const EMPTY_VIDEO_BODY: ProductionVideoBody = {
  title: "",
  synopsis: "",
  hookVo: "",
  conceptCues: [],
  ctaTextOnScreen: "",
  storyboard: [],
  props: [],
  wardrobe: [],
  locations: [],
};

export const EMPTY_STATIC_BODY: ProductionStaticBody = {
  title: "",
  synopsis: "",
  headline: "",
  panels: [],
  ctaTextOnArtwork: "",
  artDirection: { palette: [], typography: "", moodWords: [], doNots: [] },
  caption: "",
  altText: "",
  props: [],
  wardrobe: [],
  locations: [],
};

export function emptyBodyFor(kind: CalendarBriefKind): ProductionBriefBody {
  return kind === "STATIC" ? { ...EMPTY_STATIC_BODY } : { ...EMPTY_VIDEO_BODY };
}

// ---- Rows ----

export interface BriefRevision {
  id: string;
  version: number;
  body: ProductionBriefBody;
  changeNote: string | null;
  authorType: "AI" | "TEAM";
  authorName: string;
  createdAt: string;
}

/** Brief comments are flat and shared; the extras are backend #128 / #125. */
export interface BriefComment extends CommentThreadExtras {
  id: string;
  authorType: "TEAM" | "CLIENT";
  authorName: string;
  body: string;
  briefVersion: number;
  createdAt: string;
}

export interface ProductionBrief {
  id: string;
  slotId: string;
  ideaId: string;
  ideaTitle: string;
  kind: CalendarBriefKind;
  status: CalendarBriefStatus;
  /** Who signed the brief off, and who they were. Null until ACCEPTED. */
  acceptanceSource: BriefAcceptanceSource | null;
  acceptedByUserId: string | null;
  /** The live version. */
  version: number;
  /** What the client is reading. `version > sentVersion` means unsent changes. */
  sentVersion: number | null;
  generationError: string | null;
  lastEditedByUserId: string | null;
  lastEditedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** The live body, as the single most-recent revision. */
  revisions: BriefRevision[];
}

/** The live body of a brief, or null while it is still being written. */
export function liveBody(brief: ProductionBrief | null | undefined): ProductionBriefBody | null {
  if (!brief) return null;
  return (brief.revisions[0]?.body as ProductionBriefBody | undefined) ?? null;
}

export function hasUnsentChanges(brief: ProductionBrief): boolean {
  return brief.sentVersion !== null && brief.version > brief.sentVersion;
}

/** Just enough of a brief for the rail, the month grid and the pulse counts. */
export interface BriefRailInfo {
  id: string;
  slotId: string;
  status: CalendarBriefStatus;
  kind: CalendarBriefKind;
  version: number;
  sentVersion: number | null;
  /** How the UI spots a GENERATING brief that a restart stranded. */
  updatedAt: string;
}

export interface BriefRevisionDecision {
  roundNumber: number;
  decision: "APPROVED" | "DECLINED";
  reason: string | null;
  clientName: string;
  carriedOver: boolean;
  decidedAt?: string | null;
}

export interface BriefRevisionSentRound {
  roundNumber: number;
  sentAt: string;
}

export interface BriefRevisionEntry extends BriefRevision {
  sentInRounds: number[];
  /** Send events for this version, with timestamps, oldest round first. */
  sentRounds?: BriefRevisionSentRound[];
  /** Every client verdict against this version, oldest round first. */
  decisionsInRounds: BriefRevisionDecision[];
  /** Latest decision on this version — convenience for the open-decline banner. */
  decisionInRound: BriefRevisionDecision | null;
}

export interface BriefReviewRound {
  id: string;
  roundNumber: number;
  status: "PENDING" | "APPROVED" | "CHANGES_REQUESTED";
  sentAt: string;
  notifiedCount: number;
  closedAt: string | null;
  decisions: Array<{
    id: string;
    slotId: string;
    briefId: string;
    briefVersion: number;
    decision: "APPROVED" | "DECLINED";
    reason: string | null;
    clientName: string;
    carriedOver: boolean;
    decidedAt: string;
  }>;
}

export const BRIEF_STATUS_LABEL: Record<CalendarBriefStatus, string> = {
  GENERATING: "Writing",
  DRAFT: "Your draft",
  IN_REVIEW: "With client",
  CHANGES_REQUESTED: "Changes requested",
  ACCEPTED: "Approved",
  FAILED: "Needs writing",
};
